/* Custom Input Component to be used with Formik */
import React, { useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { Text, FormErrorMessage, FormFieldInfo } from 'components/atoms';
import { WidgetCheckbox } from 'components/molecules';
import { modifiersType } from 'types'
import { applyStyleModifiers } from 'styled-components-modifiers';

const TOGGLE_MODIFIERS = {
  stretch: () => `
    justify-content: space-between;
`,
}

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FieldToggle = ({
  title,
  field, // { name, value, onChange, onBlur }
  form: {
    touched, errors, isValid, isSubmitting, setFieldValue
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  className,
  info,
  modifiers,
  ...props
}) => {
  // focus on error after submit
  const fieldRef = useRef(null);
  useEffect(() => {
    if (isValid || !isSubmitting) return;
    const firstErrorKey = Object.keys(errors)[0];

    if (!isValid && firstErrorKey === field.name) {
      fieldRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isValid, errors]);

  const isError = useMemo(() => touched[field.name] && errors[field.name], [
    touched,
    errors,
    field.name,
  ]);

  const computedModidifiers = useMemo(() => {
    if (isError) return ['error'];
    return [];
  }, [isError]);

  const handleClickWrapper = () => {
    setFieldValue(field.name, !field.value)
  }

  return (
    <Wrapper
      className={className}
      onClick={handleClickWrapper}
      role="button"
      tabindex="0"
    >
      <Top modifiers={modifiers}>
        {title && <Text modifiers={['base']}>{title}</Text>}
        <WidgetCheckbox
          modifiers={computedModidifiers}
          innerRef={fieldRef}
          id={field.name}
          {...field}
          onChange={(isChecked) => {
            setFieldValue(field.name, isChecked)
          }}
          {...props}
        />
      </Top>
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </Wrapper>
  );
};

FieldToggle.propTypes = {
  title: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldValue: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
  className: PropTypes.string,
  modifiers: modifiersType,
};

FieldToggle.defaultProps = {
  title: null,
  className: '',
  info: null,
  modifiers: []
};

const Top = styled.div`
  display: flex;
  align-items: center;
  ${Text} {
    padding-right: ${props => props.theme.spacing.xs};
  }
  ${applyStyleModifiers(TOGGLE_MODIFIERS)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;
`

export default FieldToggle
